import { PlaycastTrackSet } from './typesMedia';
import { PlaycastPeerHostType, PlaycastPeerClientType } from './types';
import { createHostId, createPlayerPeerId, createRemoteControlClientId, createRemoteHostId } from '@playcastdotio/CreateId';

export const getAcceptedClientPeerTypes = (peerType: PlaycastPeerClientType): PlaycastPeerHostType[] => {
  switch (peerType) {
    case 'remoteControlClient':
      return ['remoteControlHost'];
    case 'client':
      return ['host'];
  }
};
export const getAcceptedHostPeerTypes = (peerType: PlaycastPeerHostType): PlaycastPeerClientType[] => {
  switch (peerType) {
    case 'remoteControlHost':
      return ['remoteControlClient'];
    case 'host':
      return ['client'];
  }
};

export const generateIdForPeer = (peerType: PlaycastPeerHostType | PlaycastPeerClientType) => {
  switch (peerType) {
    case 'remoteControlHost':
      return createRemoteHostId();
    case 'host':
      return createHostId();
    case 'remoteControlClient':
      return createRemoteControlClientId();
    case 'client':
      return createPlayerPeerId();
  }
};

let verbose = true;
export const setPeerLoggingVerbose = (v: boolean) => {
  verbose = v;
};

export const log = (...message: unknown[]) => {
  if (verbose) console.log('[Peer]', ...message);
};

export const debug = (...message: unknown[]) => {
  if (verbose) console.debug('[Peer]', ...message);
};

export const warn = (...message: unknown[]) => {
  console.warn('[Peer]', ...message);
};

export const deepCloneTrackState = (tracks: PlaycastTrackSet): PlaycastTrackSet => {
  return Object.fromEntries(
    Object.entries(tracks).map(([channelId, trackData]) => [
      channelId,
      {
        // MediaStreamTrack shouldn't be cloned, just referenced
        track: trackData.track,
        // Deep clone the metadata
        metadata: JSON.parse(JSON.stringify(trackData.metadata)),
      },
    ]),
  );
};

export const isAppleDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.userAgent === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS devices
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  // Check for macOS
  const isMac = /macintosh|mac os x/.test(userAgent);

  // Check for Safari browser
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

  // Check for iPad that identifies as MacOS in newer versions
  const isIPadOS = isMac && navigator.maxTouchPoints > 0;

  return isIOS || isMac || isIPadOS || isSafari;
};

export const isMobileDevice = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.userAgent === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();

  // Check for iOS devices
  const isIOS = /iphone|ipad|ipod/.test(userAgent);

  // Check for Android devices
  const isAndroid = /android/.test(userAgent);

  return isIOS || isAndroid;
};

export const captureWebrtcError = async (peerConnection: RTCPeerConnection, errorMessage: string) => {
  try {
    const statsReport = await peerConnection.getStats();
    const stats: any[] = [];
    statsReport.forEach((report) => {
      stats.push(report);
    });
    // We also want to capture the local and remote sdps
    stats.push({
      localDescription: peerConnection.localDescription,
      remoteDescription: peerConnection.remoteDescription,
    });

    // Check if Sentry is available globally
    if ((window as any).Sentry) {
      (window as any).Sentry.captureException(new Error(errorMessage), {
        normalizeDepth: 10,
        contexts: {
          WebRTC: {
            stats,
            signalingState: peerConnection.signalingState,
            iceConnectionState: peerConnection.iceConnectionState,
            connectionState: peerConnection.connectionState,
          },
        },
      });
      console.log('[Peer] WebRTC error captured:', { errorMessage, stats });
    } else {
      console.error('[Peer] Sentry not available globally:', { errorMessage, stats });
    }
  } catch (err) {
    console.error('[Peer] Failed to capture WebRTC info:', err);
  }
};

export const isFirefoxBrowser = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.userAgent === 'undefined') return false;
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('firefox');
};
